import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import api from '../services/api';
import type { AdminOrderDetail } from '../types/order';

interface FormState {
  account_type: 'consumer' | 'business';
  contact_first_name: string;
  contact_last_name: string;
  organisation_name: string;
  email: string;
  mobile_number: string;
  abn: string;
}

interface Props {
  open: boolean;
  order: AdminOrderDetail;
  onClose: () => void;
  onSuccess: () => void;
}

function initialForm(order: AdminOrderDetail): FormState {
  return {
    account_type: order.customer_type === 'business' ? 'business' : 'consumer',
    contact_first_name: order.first_name ?? '',
    contact_last_name: order.last_name ?? '',
    organisation_name: order.organisation_name ?? '',
    email: order.email ?? '',
    mobile_number: order.mobile_number ?? '',
    abn: order.abn_acn ?? '',
  };
}

export default function CreateAccountFromOrderDialog({ open, order, onClose, onSuccess }: Props) {
  const [form, setForm] = useState<FormState>(() => initialForm(order));
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    if (saving) return;
    setError(null);
    setStep('');
    setForm(initialForm(order));
    onClose();
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      setStep('Creating account…');
      const accountPayload: Record<string, unknown> = {
        account_type: form.account_type,
        contact_first_name: form.contact_first_name.trim() || undefined,
        contact_last_name: form.contact_last_name.trim() || undefined,
        email: form.email.trim() || undefined,
        mobile_number: form.mobile_number.trim() || undefined,
        date_of_birth: order.date_of_birth || undefined,
      };
      if (form.account_type === 'business') {
        if (form.organisation_name.trim()) accountPayload.organisation_name = form.organisation_name.trim();
        if (form.abn.trim()) accountPayload.abn = form.abn.trim();
      }
      const newAccount = await api.createAccount(accountPayload);

      setStep('Linking order to account…');
      await api.adminUpdateOrder(order.id, { account_id: newAccount.id });

      setStep('Linking communication history…');
      try {
        await api.adminOrderRelinkCommunications(order.id);
      } catch {
        // Non-fatal: account is created and linked. Silently continue.
      }

      setSaving(false);
      setStep('');
      setForm(initialForm(order));
      onSuccess();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Something went wrong');
      setSaving(false);
      setStep('');
    }
  };

  const isBusiness = form.account_type === 'business';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create account from order</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pre-filled from order {order.order_reference}. Review and correct any errors before creating.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Account type</InputLabel>
            <Select
              value={form.account_type}
              label="Account type"
              onChange={(e) => set('account_type', e.target.value)}
              disabled={saving}
            >
              <MenuItem value="consumer">Consumer (Residential)</MenuItem>
              <MenuItem value="business">Business</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First name"
              value={form.contact_first_name}
              onChange={(e) => set('contact_first_name', e.target.value)}
              fullWidth size="small"
              disabled={saving}
            />
            <TextField
              label="Last name"
              value={form.contact_last_name}
              onChange={(e) => set('contact_last_name', e.target.value)}
              fullWidth size="small"
              disabled={saving}
            />
          </Box>

          {isBusiness && (
            <>
              <Divider />
              <TextField
                label="Organisation name"
                value={form.organisation_name}
                onChange={(e) => set('organisation_name', e.target.value)}
                fullWidth size="small"
                disabled={saving}
              />
              <TextField
                label="ABN"
                value={form.abn}
                onChange={(e) => set('abn', e.target.value)}
                fullWidth size="small"
                disabled={saving}
                helperText="11 digits, no spaces"
              />
              <Divider />
            </>
          )}

          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            fullWidth size="small"
            disabled={saving}
          />
          <TextField
            label="Mobile number"
            value={form.mobile_number}
            onChange={(e) => set('mobile_number', e.target.value)}
            fullWidth size="small"
            disabled={saving}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 160 }}>
          {saving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">{step}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !form.contact_first_name.trim() || !form.email.trim()}
          >
            Create account
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
